import { useEffect, useState } from "react";
import { api, endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BadgeCheck, MessageSquareWarning, ShieldX, Loader2, Mic2, Building2, Megaphone, Disc3 } from "lucide-react";

/**
 * AdminVerification — unified queue for Artists, Venues, Promoters, Labels.
 *
 * The existing `/api/admin/verification/artists` endpoint already exists.
 * New in this pass: `/api/admin/verification/{venues,promoters,labels}`
 * (see backend/orgs.py). Every kind exposes the same three decision
 * endpoints (verify / request-info / reject). We keep decision behaviour
 * identical across kinds so Admin behaviour is predictable.
 */

const KIND_META = {
    artist: { label: "Artists", Icon: Mic2, listKey: "artists", collection: "artists" },
    venue: { label: "Venues", Icon: Building2, listKey: "venues", collection: "venues" },
    promoter: { label: "Promoters", Icon: Megaphone, listKey: "promoters", collection: "promoters" },
    label: { label: "Labels", Icon: Disc3, listKey: "labels", collection: "labels" },
};

const FILTERS = [
    { id: "pending", label: "Pending" },
    { id: "needs_information", label: "Needs info" },
    { id: "verified", label: "Verified" },
    { id: "rejected", label: "Rejected" },
];

export default function AdminVerification() {
    const [kind, setKind] = useState("artist");
    const [filter, setFilter] = useState("pending");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const meta = KIND_META[kind];

    const load = async () => {
        setLoading(true);
        try {
            if (kind === "artist") {
                const d = await endpoints.adminListVerification(filter);
                setRows(d.artists || []);
            } else {
                const r = await api.get(`/admin/verification/${kind}s`, { params: { status_filter: filter } });
                setRows(r.data?.[meta.listKey] || []);
            }
        } catch {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, filter]);

    const decide = async (row, action) => {
        setBusyId(row.id);
        try {
            if (kind === "artist") {
                if (action === "verify") await endpoints.adminVerifyArtist(row.id);
                else if (action === "request-info") {
                    const msg = window.prompt("Message to the artist owner:"); if (!msg) return;
                    await endpoints.adminRequestArtistInfo(row.id, msg);
                } else if (action === "reject") {
                    const reason = window.prompt("Reason for rejecting:"); if (!reason) return;
                    await endpoints.adminRejectArtist(row.id, reason);
                }
            } else {
                if (action === "verify") {
                    if (!window.confirm(`Verify "${row.name}"?`)) return;
                    await api.post(`/admin/verification/${kind}s/${row.id}/verify`);
                } else if (action === "request-info") {
                    const msg = window.prompt(`Message to ${row.name} explaining what's needed:`); if (!msg) return;
                    await api.post(`/admin/verification/${kind}s/${row.id}/request-info`, { message: msg });
                } else if (action === "reject") {
                    const reason = window.prompt(`Reason for rejecting ${row.name}:`); if (!reason) return;
                    await api.post(`/admin/verification/${kind}s/${row.id}/reject`, { reason });
                }
            }
            toast.success("Updated");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="pm-fade" data-testid="admin-verification-page">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Admin</div>
                <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Verification queue</h1>
                <p className="mt-2 text-sm text-[#a3a3a3] max-w-2xl">
                    Manual review of Artists, Venues, Promoters, and Labels.
                    Verification is separate from workspace access — rejecting does not delete the workspace.
                </p>
            </header>
            <div className="px-4 md:px-8 py-6">
                <div className="flex flex-wrap gap-2 mb-3" data-testid="admin-verif-kind-tabs">
                    {Object.entries(KIND_META).map(([k, m]) => {
                        const I = m.Icon;
                        return (
                            <button
                                key={k}
                                onClick={() => setKind(k)}
                                className={`pm-btn text-xs ${kind === k ? "pm-btn-primary" : ""}`}
                                data-testid={`admin-verif-kind-${k}`}
                            >
                                <I className="w-3 h-3 mr-1" /> {m.label}
                            </button>
                        );
                    })}
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                    {FILTERS.map((f) => (
                        <button key={f.id} onClick={() => setFilter(f.id)} className={`pm-btn text-xs ${filter === f.id ? "pm-btn-primary" : ""}`} data-testid={`admin-verif-filter-${f.id}`}>
                            {f.label}
                        </button>
                    ))}
                </div>
                {loading ? (
                    <div className="text-[10px] font-mono uppercase text-[#666]">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="border border-dashed border-[#2a2a2a] p-8 text-center text-sm text-[#a3a3a3]" data-testid="admin-verif-empty">
                        Nothing here right now.
                    </div>
                ) : (
                    <div className="border border-[#2a2a2a]">
                        {rows.map((row) => {
                            const v = row.verification || {};
                            return (
                                <div key={row.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start p-4 border-b border-[#2a2a2a] last:border-b-0" data-testid={`admin-verif-row-${row.id}`}>
                                    <div className="md:col-span-4 flex gap-3 items-start min-w-0">
                                        <div className="w-14 h-14 border border-[#2a2a2a] shrink-0 flex items-center justify-center bg-[#141414]">
                                            <meta.Icon className="w-5 h-5 text-[#ff4400]" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-display font-bold uppercase text-sm truncate">{row.name}</div>
                                            <div className="text-[10px] font-mono uppercase text-[#a3a3a3] mt-1">{row.city || "—"}{row.country ? `, ${row.country}` : ""}</div>
                                            <div className="text-[10px] font-mono uppercase text-[#666]">{meta.label.slice(0, -1)}</div>
                                        </div>
                                    </div>
                                    <div className="md:col-span-4 text-[11px] font-mono text-[#a3a3a3] space-y-1">
                                        <div><span className="text-[#666]">Submitted by:</span> {v.submitted_by_name || v.submitted_by_email || "—"}</div>
                                        <div><span className="text-[#666]">Email:</span> {v.submitted_by_email || "—"}</div>
                                        <div><span className="text-[#666]">Submitted:</span> {v.submitted_at ? new Date(v.submitted_at).toLocaleString() : "—"}</div>
                                        <div><span className="text-[#666]">Status:</span> <span className="text-[#ff4400]">{v.status}</span></div>
                                        {v.admin_message && <div className="text-[#ff9500]">Admin msg: {v.admin_message}</div>}
                                        {v.decision_reason && <div className="text-[#ff3333]">Reason: {v.decision_reason}</div>}
                                    </div>
                                    <div className="md:col-span-4 flex flex-wrap justify-end gap-1">
                                        <button onClick={() => decide(row, "verify")} disabled={busyId === row.id} className="pm-btn pm-btn-primary text-[10px]" data-testid={`admin-verif-verify-${row.id}`}>
                                            {busyId === row.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <BadgeCheck className="w-3 h-3 mr-1" />} Verify
                                        </button>
                                        <button onClick={() => decide(row, "request-info")} disabled={busyId === row.id} className="pm-btn text-[10px]" data-testid={`admin-verif-info-${row.id}`}>
                                            <MessageSquareWarning className="w-3 h-3 mr-1" /> Request info
                                        </button>
                                        <button onClick={() => decide(row, "reject")} disabled={busyId === row.id} className="pm-btn text-[10px] text-[#ff3333] border-[#ff3333]" data-testid={`admin-verif-reject-${row.id}`}>
                                            <ShieldX className="w-3 h-3 mr-1" /> Reject
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
